import { useEffect, useRef, useState } from "react";

// Cifra que se anima de su valor anterior al nuevo (requestAnimationFrame + easeOutCubic).
//
// Vivía dentro de `DashboardPage.jsx` con un comentario que decía "Solo dashboard", y el resultado
// era que las mismas cifras cambiaban con animación en el Dashboard y de golpe en Propiedades,
// Liquidaciones y Reportes. La animación no es adorno: dice que el número SE MOVIÓ, que es
// justamente lo que pasa al cambiar de período o de filtro.
//
// La última cifra mostrada vive en un ref para que un cambio a mitad de animación arranque de donde
// va la cuenta y no del valor viejo: sin eso, mover el mes dos veces seguidas produce un salto.

const formatCurrency = (value) =>
  new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(value || 0);

/** Formato de la cifra animada según su tipo: `currency` ($), `percent` (%) o `int`. */
export const formatMetric = (n, type) => {
  const v = Math.round(n);
  if (type === "currency") return formatCurrency(v);
  if (type === "percent") return `${v}%`;
  return v.toLocaleString("es-CL");
};

// `format` existe para las cifras que no entran en los tres tipos: la tasa de ocupación se muestra
// con un decimal cuando lo tiene, y animarla como `percent` (que redondea) le borraría ese decimal
// — o sea que animar cambiaría el dato, no sólo cómo aparece.
export function CountUp({ value, type = "int", durationMs = 550, format }) {
  const target = Number(value) || 0;
  const [display, setDisplay] = useState(target);
  const displayRef = useRef(target);
  const rafRef = useRef(null);

  useEffect(() => {
    const from = displayRef.current;
    const to = target;
    if (from === to) { setDisplay(to); return undefined; }
    let start = null;
    const tick = (now) => {
      if (start === null) start = now;
      const p = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      const current = from + (to - from) * eased;
      displayRef.current = current;
      setDisplay(current);
      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        displayRef.current = to;
        setDisplay(to);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, durationMs]);

  return <>{format ? format(display) : formatMetric(display, type)}</>;
}
